import { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Camera, ArrowLeft, Radio } from 'lucide-react'
import {
  createCattleEvent, getVaccines, getTreatments,
  getDeathCauses, getHerds, getAnimal, getBreeds, uploadMedia
} from '../../services/api'
import RfidScanModal from '../../components/RfidScanModal'
import useUnsavedChanges from '../../hooks/useUnsavedChanges'
import useSubmitLock from '../../hooks/useSubmitLock'
import { useAuth } from '../../contexts/AuthContext'

export default function EventForm({ ranchId }) {
  const navigate = useNavigate()
  const { userId } = useAuth()
  const { animalId } = useParams()
  const location = useLocation()
  const queryClient = useQueryClient()
  
  // Get default event type from location state
  const defaultType = location.state?.defaultType

  const [eventType, setEventType] = useState(defaultType || 'treatment')
  const [eventDate, setEventDate] = useState(new Date().toISOString().split('T')[0])
  const [eventData, setEventData] = useState({})
  const [photos, setPhotos] = useState([])
  const [uploadingPhotos, setUploadingPhotos] = useState(0)
  const [error, setError] = useState('')
  const [isDirty, setIsDirty] = useState(false)

  useUnsavedChanges(isDirty)

  // Always fetch full animal data from API (location.state may have incomplete data)
  const { data: animal } = useQuery({
    queryKey: ['animal', animalId],
    queryFn: () => getAnimal(animalId),
    enabled: !!animalId,
  })

  // Fetch reference data
  const { data: vaccines } = useQuery({
    queryKey: ['vaccines'],
    queryFn: getVaccines,
    enabled: eventType === 'vaccination',
  })

  const { data: treatments } = useQuery({
    queryKey: ['treatments'],
    queryFn: () => getTreatments(),
    enabled: eventType === 'treatment',
  })

  const { data: deathCauses } = useQuery({
    queryKey: ['deathCauses'],
    queryFn: getDeathCauses,
    enabled: eventType === 'death',
  })

  const { data: herds } = useQuery({
    queryKey: ['herds'],
    queryFn: getHerds,
  })

  const { data: breeds } = useQuery({
    queryKey: ['breeds'],
    queryFn: getBreeds,
    enabled: eventType === 'calving',
  })

  // Helper to format API error messages
  const formatError = (err) => {
    const detail = err.response?.data?.detail
    if (!detail) return 'An error occurred'
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail)) {
      return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ')
    }
    return 'An error occurred'
  }

  const { locked, lock, unlock } = useSubmitLock()

  // Create event mutation
  const createMutation = useMutation({
    mutationFn: createCattleEvent,
    onSuccess: () => {
      queryClient.invalidateQueries(['cattle'])
      queryClient.invalidateQueries(['cattleEvents'])
      handleClose()
    },
    onError: (err) => setError(formatError(err)),
    onSettled: unlock,
  })

  // Reset event data when type changes
  useEffect(() => {
    setEventData({})
    setPhotos([])
  }, [eventType])

  // Close/Cancel handler - navigate back
  const handleClose = () => {
    if (animalId) {
      navigate(`/cattle/${animalId}`)
    } else {
      navigate('/cattle')
    }
  }

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files)
    setUploadingPhotos(prev => prev + files.length)
    for (const file of files) {
      try {
        const result = await uploadMedia(file, {
          context: 'event_evidence',
          ranch_id: ranchId,
        })
        setPhotos(prev => [...prev, {
          name: file.name,
          url: result.url,
          photoId: result.id,
          timestamp: new Date().toISOString(),
        }])
      } catch (err) {
        setError(`Failed to upload ${file.name}: ${err.response?.data?.detail || err.message}`)
      } finally {
        setUploadingPhotos(prev => prev - 1)
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!lock()) return

    const base = {
      animal_id: animalId,
      ranch_id: ranchId,
      event_date: eventDate,
      reported_by: userId,
      report_channel: 'form',
      evidence_urls: photos.map(p => p.url),
    }

    // Treatment: one animal can receive several drugs at once. Fan the line
    // items out to N cattle_events sharing the date + evidence — each keeps the
    // scalar treatment_id/dosage shape that drug inventory + reports read, so
    // nothing downstream changes.
    if (eventType === 'treatment') {
      const lines = (eventData.treatments && eventData.treatments.length
        ? eventData.treatments
        : [eventData]).filter(l => l && l.treatment_id)
      if (lines.length === 0) {
        setError('Add at least one treatment.')
        unlock()
        return
      }
      try {
        for (const line of lines) {
          const { standard_dosage_per_kg, ...event_data } = line
          await createCattleEvent({ ...base, event_type: 'treatment', event_data })
        }
        queryClient.invalidateQueries(['cattle'])
        queryClient.invalidateQueries(['cattleEvents'])
        handleClose()
      } catch (err) {
        setError(formatError(err))
      } finally {
        unlock()
      }
      return
    }

    createMutation.mutate({ ...base, event_type: eventType, event_data: eventData })
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header with close button */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Record Event</h1>
            {animal && (
              <p className="text-gray-600">
                {animal.local_name || animal.rfid_tag} ({animal.category})
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={handleClose}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} onChange={() => setIsDirty(true)} className="space-y-6">
        {/* Event Type Selection */}
        <div className="bg-white rounded-lg shadow p-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">Event Type *</label>
          <div className="grid grid-cols-4 gap-2">
            {[
              { value: 'treatment', label: 'Treatment' },
              { value: 'vaccination', label: 'Vaccination' },
              { value: 'weighing', label: 'Weight' },
              { value: 'calving', label: 'Calving' },
              { value: 'death', label: 'Death' },
              { value: 'sale', label: 'Sale' },
              { value: 'lost', label: 'Lost' },
              { value: 'observation', label: 'Observation' },
            ].map(type => (
              <button
                key={type.value}
                type="button"
                onClick={() => setEventType(type.value)}
                className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  eventType === type.value
                    ? 'bg-green-600 text-white border-green-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>

        {/* Event Date */}
        <div className="bg-white rounded-lg shadow p-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
          <input
            type="date"
            required
            value={eventDate}
            onChange={(e) => setEventDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          />
        </div>

        {/* Dynamic Fields Based on Event Type */}
        {eventType === 'vaccination' && (
          <VaccinationFields 
            vaccines={vaccines} 
            eventData={eventData} 
            setEventData={setEventData} 
          />
        )}

        {eventType === 'treatment' && (
          <TreatmentFields 
            treatments={treatments} 
            eventData={eventData} 
            setEventData={setEventData}
            animalWeight={animal?.last_weight_kg}
          />
        )}

        {eventType === 'weighing' && (
          <WeightFields eventData={eventData} setEventData={setEventData} />
        )}

        {eventType === 'calving' && (
          <CalvingFields
            eventData={eventData}
            setEventData={setEventData}
            damId={animalId}
            herds={herds}
            breeds={breeds}
            dam={animal}
            ranchId={ranchId}
          />
        )}

        {eventType === 'death' && (
          <DeathFields 
            deathCauses={deathCauses} 
            eventData={eventData} 
            setEventData={setEventData} 
          />
        )}

        {eventType === 'sale' && (
          <SaleFields eventData={eventData} setEventData={setEventData} />
        )}

        {eventType === 'lost' && (
          <LostFields eventData={eventData} setEventData={setEventData} />
        )}

        {eventType === 'observation' && (
          <ObservationFields eventData={eventData} setEventData={setEventData} />
        )}

        {/* Photo Upload */}
        <div className="bg-white rounded-lg shadow p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Photos {['calving', 'death'].includes(eventType) && <span className="text-red-500">*</span>}
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoUpload}
              className="hidden"
              id="photo-upload"
            />
            <label
              htmlFor="photo-upload"
              className="flex flex-col items-center cursor-pointer"
            >
              <Camera className="h-8 w-8 text-gray-400 mb-2" />
              <span className="text-sm text-gray-600">Click to upload photos</span>
              <span className="text-xs text-gray-400">GPS and timestamp will be captured</span>
            </label>
          </div>
          {uploadingPhotos > 0 && (
            <p className="text-sm text-blue-600 mt-2">Uploading {uploadingPhotos} photo{uploadingPhotos > 1 ? 's' : ''}...</p>
          )}
          {photos.length > 0 && (
            <div className="flex gap-2 mt-3 flex-wrap">
              {photos.map((photo, idx) => (
                <div key={idx} className="relative">
                  <img
                    src={photo.url}
                    alt={`Photo ${idx + 1}`}
                    className="h-16 w-16 object-cover rounded"
                  />
                  <button
                    type="button"
                    onClick={() => setPhotos(prev => prev.filter((_, i) => i !== idx))}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={locked || createMutation.isPending || uploadingPhotos > 0}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            {createMutation.isPending ? 'Saving...' : uploadingPhotos > 0 ? 'Uploading photos...' : 'Save Event'}
          </button>
        </div>
      </form>
    </div>
  )
}

// =============================================================================
// SUB-COMPONENTS FOR EACH EVENT TYPE
// =============================================================================

function VaccinationFields({ vaccines, eventData, setEventData }) {
  const selectedVaccine = vaccines?.find(v => v.id === eventData.vaccine_id)

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="font-medium text-gray-900 mb-4">Vaccination Details</h3>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Vaccine *</label>
          <select
            required
            value={eventData.vaccine_id || ''}
            onChange={(e) => {
              const vaccine = vaccines?.find(v => v.id === e.target.value)
              setEventData({
                ...eventData,
                vaccine_id: e.target.value,
                vaccine_name: vaccine?.name,
                manufacturer: vaccine?.manufacturer,
                dosage_unit: vaccine?.dosage_unit,
                dosage: vaccine?.standard_dosage,
              })
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">Select vaccine</option>
            {vaccines?.map(v => (
              <option key={v.id} value={v.id}>
                {v.name} {v.manufacturer ? `(${v.manufacturer})` : ''}
              </option>
            ))}
          </select>
        </div>

        {selectedVaccine?.name === 'Other' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Specify Vaccine *</label>
            <input
              type="text"
              required
              value={eventData.vaccine_name_other || ''}
              onChange={(e) => setEventData({ ...eventData, vaccine_name_other: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="Enter vaccine name"
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Dosage *</label>
            <input
              type="number"
              required
              step="0.1"
              min="0"
              value={eventData.dosage || ''}
              onChange={(e) => setEventData({ ...eventData, dosage: parseFloat(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
            <input
              type="text"
              value={eventData.dosage_unit || 'ml'}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Batch Number</label>
          <input
            type="text"
            value={eventData.batch_number || ''}
            onChange={(e) => setEventData({ ...eventData, batch_number: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="VAC-2026-XXX"
          />
        </div>
      </div>
    </div>
  )
}

function TreatmentFields({ treatments, eventData, setEventData, animalWeight }) {
  // One animal can get several drugs in one handling. Manage a list of
  // line-items; handleSubmit fans each out to its own cattle_event.
  const lines = (eventData.treatments && eventData.treatments.length)
    ? eventData.treatments
    : [{}]

  const updateLine = (i, patch) =>
    setEventData({ ...eventData, treatments: lines.map((l, idx) => idx === i ? { ...l, ...patch } : l) })
  const addLine = () => setEventData({ ...eventData, treatments: [...lines, {}] })
  const removeLine = (i) =>
    setEventData({ ...eventData, treatments: lines.filter((_, idx) => idx !== i) })

  const selectTreatment = (i, treatmentId) => {
    const t = treatments?.find(x => x.id === treatmentId)
    const dosage = (t?.standard_dosage_per_kg && animalWeight)
      ? parseFloat((t.standard_dosage_per_kg * animalWeight).toFixed(1))
      : lines[i]?.dosage
    updateLine(i, {
      treatment_id: treatmentId,
      treatment_name: t?.treatment_name,
      drug_name: t?.drug_name,
      manufacturer: t?.manufacturer,
      dosage_unit: t?.dosage_unit,
      standard_dosage_per_kg: t?.standard_dosage_per_kg,
      withdrawal_period_days: t?.withdrawal_period_days,
      dosage,
    })
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-gray-900">Treatment Details</h3>
        <span className="text-xs text-gray-500">
          {lines.filter(l => l.treatment_id).length} selected
        </span>
      </div>

      <div className="space-y-6">
        {lines.map((line, i) => {
          const selected = treatments?.find(t => t.id === line.treatment_id)
          return (
            <div key={i} className={i > 0 ? 'pt-6 border-t border-gray-200' : ''}>
              <div className="flex items-start gap-2">
                <div className="flex-1 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Treatment{lines.length > 1 ? ` #${i + 1}` : ''} *
                    </label>
                    <select
                      required
                      value={line.treatment_id || ''}
                      onChange={(e) => selectTreatment(i, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Select treatment</option>
                      {treatments?.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.treatment_name} {t.drug_name ? `- ${t.drug_name}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selected?.treatment_name === 'Other' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Specify Treatment *</label>
                      <input
                        type="text"
                        required
                        value={line.treatment_name_other || ''}
                        onChange={(e) => updateLine(i, { treatment_name_other: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Dosage *</label>
                      <input
                        type="number" required step="0.1" min="0"
                        value={line.dosage ?? ''}
                        onChange={(e) => updateLine(i, { dosage: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                      <input type="text" value={line.dosage_unit || 'ml'} readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Batch #</label>
                      <input type="text" value={line.batch_number || ''}
                        onChange={(e) => updateLine(i, { batch_number: e.target.value })}
                        placeholder="Optional"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                    </div>
                  </div>

                  {animalWeight && selected?.standard_dosage_per_kg && (
                    <p className="text-xs text-gray-500">
                      Calculated: {(selected.standard_dosage_per_kg * animalWeight).toFixed(1)} {selected.dosage_unit} for {animalWeight}kg
                    </p>
                  )}

                  {selected?.withdrawal_period_days > 0 && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                      ⚠️ Withdrawal period: {selected.withdrawal_period_days} days
                    </div>
                  )}
                </div>

                {lines.length > 1 && (
                  <button type="button" onClick={() => removeLine(i)}
                    title="Remove treatment"
                    className="mt-7 p-2 text-gray-400 hover:text-red-600">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <button type="button" onClick={addLine}
        className="mt-4 text-sm font-medium text-green-700 hover:text-green-800">
        + Add another treatment
      </button>
    </div>
  )
}

function WeightFields({ eventData, setEventData }) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="font-medium text-gray-900 mb-4">Weight Recording</h3>
      
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Weight (kg) *</label>
            <input
              type="number"
              required
              step="0.1"
              min="0"
              value={eventData.weight_kg || ''}
              onChange={(e) => setEventData({ ...eventData, weight_kg: parseFloat(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Method *</label>
            <select
              required
              value={eventData.weight_method || 'scale'}
              onChange={(e) => setEventData({ ...eventData, weight_method: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="scale">Weighing Scale</option>
              <option value="estimate">Visual Estimate</option>
              <option value="tape">Weigh Tape</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Body Condition Score (1-5)</label>
          <select
            value={eventData.body_condition_score || ''}
            onChange={(e) => setEventData({ ...eventData, body_condition_score: parseFloat(e.target.value) })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">Not recorded</option>
            <option value="1">1 - Emaciated</option>
            <option value="1.5">1.5</option>
            <option value="2">2 - Thin</option>
            <option value="2.5">2.5</option>
            <option value="3">3 - Good</option>
            <option value="3.5">3.5</option>
            <option value="4">4 - Fat</option>
            <option value="4.5">4.5</option>
            <option value="5">5 - Obese</option>
          </select>
        </div>
      </div>
    </div>
  )
}

function CalvingFields({ eventData, setEventData, damId, herds, breeds, dam, ranchId }) {
  const [scanModalOpen, setScanModalOpen] = useState(false)

  const handleCalfRfidScanned = (rfidTag) => {
    setEventData({ ...eventData, calf_rfid_tag: rfidTag })
    setScanModalOpen(false)
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="font-medium text-gray-900 mb-4">Calving Details</h3>
      <p className="text-sm text-gray-600 mb-4">Register the new calf born to this animal</p>

      {/* RFID Scan Modal for Calf */}
      <RfidScanModal
        isOpen={scanModalOpen}
        onClose={() => setScanModalOpen(false)}
        onNewRfid={handleCalfRfidScanned}
        mode="register"
        ranchId={ranchId}
      />

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Calf RFID Tag *</label>
            <div className="flex gap-2">
              <input
                type="text"
                required
                value={eventData.calf_rfid_tag || ''}
                onChange={(e) => setEventData({ ...eventData, calf_rfid_tag: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="UG-2026-XXX"
              />
              <button
                type="button"
                onClick={() => setScanModalOpen(true)}
                className="px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                title="Scan calf's RFID tag"
              >
                <Radio className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Calf Sex *</label>
            <select
              required
              value={eventData.calf_sex || ''}
              onChange={(e) => setEventData({ ...eventData, calf_sex: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Select</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Calf Local Name</label>
            <input
              type="text"
              value={eventData.calf_local_name || ''}
              onChange={(e) => setEventData({ ...eventData, calf_local_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="e.g., Spotted One"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Calf Visual Tag ID</label>
            <input
              type="text"
              value={eventData.calf_tag_id || ''}
              onChange={(e) => setEventData({ ...eventData, calf_tag_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="e.g., 001, A-15"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Breed *</label>
            <select
              required
              value={eventData.calf_breed_id || ''}
              onChange={(e) => {
                const breed = breeds?.find(b => b.id === e.target.value)
                setEventData({
                  ...eventData,
                  calf_breed_id: e.target.value,
                  calf_breed_name: breed?.name || null
                })
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Select breed</option>
              {breeds?.map(breed => (
                <option key={breed.id} value={breed.id}>{breed.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Breed Purity</label>
            <select
              value={eventData.calf_breed_purity || ''}
              onChange={(e) => setEventData({ ...eventData, calf_breed_purity: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Unknown</option>
              <option value="purebred">Purebred</option>
              <option value="crossbred">Crossbred</option>
              <option value="grade">Grade</option>
              <option value="f1">F1 Cross</option>
              <option value="f2">F2 Cross</option>
              <option value="three_quarter">3/4 Breed</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Breed Notes</label>
          <textarea
            value={eventData.calf_breed_notes || ''}
            onChange={(e) => setEventData({ ...eventData, calf_breed_notes: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            rows={2}
            placeholder="e.g., Dam is Ankole, Sire is Boran..."
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assign to Herd *</label>
            <select
              required
              value={eventData.calf_herd || 'Main'}
              onChange={(e) => setEventData({ ...eventData, calf_herd: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              {herds?.map(h => (
                <option key={h.value} value={h.value}>{h.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Owner / Segment</label>
            {dam?.owner_segment ? (
              <>
                <div className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-gray-700">
                  {dam.owner_segment}
                </div>
                <p className="text-xs text-gray-500 mt-1">Inherited from dam (can be changed later)</p>
              </>
            ) : (
              <>
                <div className="w-full px-3 py-2 border border-gray-200 bg-gray-100 rounded-lg text-gray-400 italic">
                  Not set on dam
                </div>
                <p className="text-xs text-gray-500 mt-1">Set owner/segment on dam first, or edit calf later</p>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Birth Weight (kg)</label>
            <input
              type="number"
              step="0.1"
              min="0"
              value={eventData.calf_weight_kg || ''}
              onChange={(e) => setEventData({ ...eventData, calf_weight_kg: parseFloat(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Birth Complications</label>
            <select
              value={eventData.birth_complications || 'none'}
              onChange={(e) => setEventData({ ...eventData, birth_complications: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="none">None - Normal delivery</option>
              <option value="assisted">Assisted delivery</option>
              <option value="difficult">Difficult birth (dystocia)</option>
              <option value="cesarean">Cesarean section</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            value={eventData.calf_notes || ''}
            onChange={(e) => setEventData({ ...eventData, calf_notes: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            rows={2}
            placeholder="Any additional notes about the calf..."
          />
        </div>

        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
          📝 Dam ID will be automatically set. The calf will be added to the herd automatically.
        </div>
      </div>
    </div>
  )
}

function DeathFields({ deathCauses, eventData, setEventData }) {
  const selectedCause = deathCauses?.find(c => c.id === eventData.cause_id)

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="font-medium text-gray-900 mb-4">Death Details</h3>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Cause of Death *</label>
          <select
            required
            value={eventData.cause_id || ''}
            onChange={(e) => {
              const cause = deathCauses?.find(c => c.id === e.target.value)
              setEventData({
                ...eventData,
                cause_id: e.target.value,
                cause: cause?.cause,
                cause_category: cause?.category,
              })
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">Select cause</option>
            {deathCauses?.map(c => (
              <option key={c.id} value={c.id}>
                {c.cause} ({c.category})
              </option>
            ))}
          </select>
        </div>

        {selectedCause?.requires_description && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Specify Details *</label>
            <input
              type="text"
              required
              value={eventData.cause_detail || ''}
              onChange={(e) => setEventData({ ...eventData, cause_detail: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="Provide more details"
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
          <textarea
            value={eventData.notes || ''}
            onChange={(e) => setEventData({ ...eventData, notes: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            rows={3}
            placeholder="Location found, condition, any other observations..."
          />
        </div>

        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
          ⚠️ A photo of the deceased animal is required for verification.
        </div>
      </div>
    </div>
  )
}

function SaleFields({ eventData, setEventData }) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="font-medium text-gray-900 mb-4">Sale Details</h3>
      
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sale Weight (kg) *</label>
            <input
              type="number"
              required
              step="0.1"
              min="0"
              value={eventData.weight_kg || ''}
              onChange={(e) => setEventData({ ...eventData, weight_kg: parseFloat(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sale Price (UGX) *</label>
            <input
              type="number"
              required
              min="0"
              value={eventData.sale_price || ''}
              onChange={(e) => setEventData({ ...eventData, sale_price: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Buyer Name</label>
          <input
            type="text"
            value={eventData.buyer_name || ''}
            onChange={(e) => setEventData({ ...eventData, buyer_name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Buyer Contact</label>
          <input
            type="tel"
            value={eventData.buyer_contact || ''}
            onChange={(e) => setEventData({ ...eventData, buyer_contact: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="+256..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
          <select
            required
            value={eventData.payment_method || ''}
            onChange={(e) => setEventData({ ...eventData, payment_method: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">Select method</option>
            <option value="cash">Cash</option>
            <option value="mobile_money">Mobile Money</option>
            <option value="bank_transfer">Bank Transfer</option>
          </select>
        </div>

        {eventData.sale_price && eventData.weight_kg && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
            💰 Price per kg: UGX {Math.round(eventData.sale_price / eventData.weight_kg).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  )
}

function LostFields({ eventData, setEventData }) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="font-medium text-gray-900 mb-4">Lost Animal Details</h3>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes <span className="text-red-500">*</span>
          </label>
          <textarea
            required
            value={eventData.notes || ''}
            onChange={(e) => setEventData({ ...eventData, notes: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            rows={4}
            placeholder="Describe circumstances of loss, last known location, search efforts, etc."
          />
        </div>

        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
          ⚠️ Recording this event will change the animal's status to "Lost". The event date above serves as the date reported.
        </div>
      </div>
    </div>
  )
}

function ObservationFields({ eventData, setEventData }) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="font-medium text-gray-900 mb-4">Observation</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select
            value={eventData.category || ''}
            onChange={(e) => setEventData({ ...eventData, category: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">General</option>
            <option value="health">Health / disease</option>
            <option value="lab_result">Lab / test result</option>
            <option value="body_condition">Body condition</option>
            <option value="behaviour">Behaviour</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes <span className="text-red-500">*</span>
          </label>
          <textarea
            required
            value={eventData.notes || ''}
            onChange={(e) => setEventData({ ...eventData, notes: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            rows={4}
            placeholder="e.g. Brucellosis RBT positive (++) — flag for cull. Attach the lab report in Photos below."
          />
        </div>
        <p className="text-xs text-gray-500">
          Attach photos/scans (e.g. the lab report) in the Photos section below.
        </p>
      </div>
    </div>
  )
}
